import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Spinner,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { getBrandsForExporter } from 'common/src/api/market_data';
import {
  saveFavoriteBrands,
  loadFavoriteBrands,
} from 'common/src/api/presettlement-settings';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import useUserStore from 'common/src/stores/useUserStore';
import { supabase } from 'common/src/supabase';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert } from '../ui';

export const FavoriteBrandsSection = () => {
  const { t } = useTranslation('settings');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myBrands, setMyBrands] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  // Lo último guardado: Guardar se activa solo si hay cambios.
  const [savedBrands, setSavedBrands] = useState<string[]>([]);
  // Temporada de la que se heredó la lista guardada (null si es propia).
  const [inheritedFrom, setInheritedFrom] = useState<string | null>(null);

  const userExporter = userProfile?.exporter;

  // Load user's brands
  useEffect(() => {
    const loadData = async () => {
      if (!userExporter?.excel_name) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const brands = await getBrandsForExporter(
          userExporter.excel_name,
          selectedSpecies?.id,
          selectedSeason
        );
        setMyBrands(brands);
      } catch (error) {
        console.error('Error loading brands:', error);
        toast.error(t('Error al cargar etiquetas'));
      } finally {
        setLoading(false);
      }
    };

    if (selectedSpecies?.id && selectedSeason) {
      loadData();
    }
  }, [selectedSpecies?.id, selectedSeason, userExporter?.excel_name, t]);

  // Load user's favorite brands
  useEffect(() => {
    const loadFavorites = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason)
        return;

      try {
        const session = await supabase.auth.getSession();
        const favorites = await loadFavoriteBrands(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          session.data.session
        );
        setSelectedBrands(favorites.values);
        setSavedBrands(favorites.values);
        setInheritedFrom(favorites.inheritedFrom);
      } catch (error) {
        console.error('Error loading favorite brands:', error);
      }
    };

    loadFavorites();
  }, [userProfile?.exporter_id, selectedSpecies?.id, selectedSeason]);

  const hasChanges =
    [...selectedBrands].sort().join('|') !== [...savedBrands].sort().join('|');

  // Save favorites to database
  const handleSave = async () => {
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason)
      return;

    setSaving(true);
    try {
      const session = await supabase.auth.getSession();
      const success = await saveFavoriteBrands(
        userProfile.exporter_id,
        selectedSeason,
        selectedSpecies.id,
        selectedBrands,
        session.data.session
      );

      if (success) {
        setSavedBrands(selectedBrands);
        setInheritedFrom(null);
        toast.success(t('Etiquetas a seguir guardadas exitosamente'));
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving favorite brands:', error);
      toast.error(t('Error al guardar etiquetas a seguir'));
    } finally {
      setSaving(false);
    }
  };

  if (!userExporter) {
    return (
      <Card>
        <CardBody>
          <p className='text-center text-default-400'>
            {t('No hay exportadora asignada a tu perfil')}
          </p>
        </CardBody>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <p className='text-lg font-bold'>{t('Tus Etiquetas a Seguir')}</p>
      </div>

      {inheritedFrom && (
        <Alert showIcon className='mb-4'>
          {t(
            'Heredada de la temporada {{season}}. Si la cambias, los cambios quedan solo para {{current}}.',
            { season: inheritedFrom, current: selectedSeason }
          )}
        </Alert>
      )}

      {myBrands.length === 0 ? (
        <Card>
          <CardBody>
            <p className='text-center text-default-400'>
              {t(
                'No hay etiquetas registradas para tu exportadora en la temporada seleccionada'
              )}
            </p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <CheckboxGroup
              value={selectedBrands}
              onValueChange={setSelectedBrands}
              className='gap-2'
            >
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                {myBrands.map((brand) => (
                  <Checkbox key={brand} value={brand}>
                    {brand}
                  </Checkbox>
                ))}
              </div>
            </CheckboxGroup>
            <div className='mt-4 pt-4 border-t border-divider flex items-center justify-between gap-4'>
              <p className='text-sm text-default-500'>
                {selectedBrands.length > 0 &&
                  `${selectedBrands.length} ${t('etiquetas seleccionadas')}`}
              </p>
              <Button
                color='primary'
                onPress={handleSave}
                isLoading={saving}
                isDisabled={!hasChanges}
              >
                {t('Guardar')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
